import React, { FC, useEffect } from 'react';
import { CloseThin } from '~/components/Icons/fill';
import { Modals, Product, SelectedProductsState } from '~/types/products';
import { trackEvent } from '~/helpers/tracking';
import { UpsellStyled } from './UpsellStyled';
import Button from '~/components/Button';
import ColumnTiles from '~/components/ColumnTiles';
import getAsset from '~/helpers/getAsset';
import { useSelector } from 'react-redux';
import { ReduxState } from '~/types/redux';
import { NETFLIX_BUNDLE } from '~/constants/entityIds';



interface Props {
  selectedState: SelectedProductsState;
  setSelectedState: (products: SelectedProductsState) => void;
  handleSelectProduct: (product: Product, redirect?: boolean, selectWifiSecureFreeTrial?: boolean, selectNetflixUpsellBundle?: boolean) => void;
  setActiveModal: (key?: Modals) => void;
  visible: boolean;
}


const UpsellModal: FC<Props> = ({
  setActiveModal,
  selectedState,
  handleSelectProduct,
  visible,
}) => {
  const { products } = useSelector((store: ReduxState) => store);
  const netflixWifiBundle = products?.available?.video?.find((product) => product.productId === NETFLIX_BUNDLE);
  const netflixPrice = netflixWifiBundle?.price?.amount

  const selectedWHWF = selectedState?.vas?.WHOLE_HOME_WIFI;
  const selectedWFS = selectedState?.vas?.WIFI_SECURITY;

  useEffect(() => {

    if (visible) {
      let eventType, eventText;

      if (selectedWHWF?.name && selectedWFS?.name) {
        eventType = 'NETFLIX Modal opened - WHWF + WFS';
        eventText = `Netflix Upsell Modal opened - ${selectedWFS?.name} & ${selectedWHWF?.name} selected`;
      } else if (selectedWHWF?.name) {
        eventType = 'NETFLIX Modal opened - WHWF';
        eventText = `Netflix Upsell Modal opened - ${selectedWHWF?.name} selected`;
      } else if (selectedWFS?.name) {
        eventType = 'NETFLIX Modal opened - WFS';
        eventText = `Netflix Upsell Modal opened - ${selectedWFS?.name} selected`;
      }

      trackEvent({
        action: 'elementViewed',
        data: {
          elementType: eventType,
          location: 'plan-package',
          position: 'Netflix Upsell Modal',
          text: eventText,
         },
        });
    }
  }, [visible]);

  const handleReplace = () => {
    // Delete WHWF and WFS and add netflix bundle
    delete selectedState?.vas?.WHOLE_HOME_WIFI;
    delete selectedState?.vas?.WIFI_SECURITY;

    let actionOutcomeText;

      if (selectedWFS && selectedWHWF) {
        actionOutcomeText = `Switch to Bundle Selected by ${selectedWFS?.name} + ${selectedWHWF?.name}`;
      } else if (selectedWHWF) {
        actionOutcomeText = `Switch to Bundle Selected by ${selectedWHWF?.name}`;
      } else if (selectedWFS) {
        actionOutcomeText = `Switch to Bundle Selected by ${selectedWFS?.name}`;
      }

      trackEvent({
        action: 'elementClicked',
        data: {
          actionOutcome: actionOutcomeText,
          elementType: 'Netflix Upsell Modal - Switch to bundle',
          location: 'plan-package',
          position: 'Netflix Upsell Modal',
          text: actionOutcomeText,
        },
      });

    setActiveModal(); // Close modal after update
    handleSelectProduct(selectedState?.internet, true, false, true);
  };

  const handleSelectCurrentVas = () => {
    setActiveModal(); // Close modal after update
    handleSelectProduct(selectedState?.internet)
  };

  const handleCloseModal = () => {
    let actionOutcomeText;

    if (selectedWFS && selectedWHWF) {
      actionOutcomeText = `Modal closed after selecting ${selectedWFS?.name} + ${selectedWHWF?.name}`;
    } else if (selectedWHWF) {
      actionOutcomeText = `Modal closed after selecting ${selectedWHWF?.name}`;
    } else if (selectedWFS) {
      actionOutcomeText = `Modal closed after selecting ${selectedWFS?.name}`;
    }

    trackEvent({
      action: 'elementClicked',
      data: {
        actionOutcome: actionOutcomeText,
        elementType: 'Exit Netflix Upsell Modal',
        location: 'plan-package',
        position: 'Netflix Upsell Modal',
        text: actionOutcomeText,
      }
    });
    // Close modal after update
    setActiveModal();
  }

  if (!visible) return null;

  const { name } = selectedWHWF || selectedWFS;
  const title = 'Excellent choice!';
  const titleDescription = `You’ve selected ${name}, which is also included in the Netflix & Pro Wi-Fi Bundle.`;
  const description = 'Would you like to switch to the full bundle?';

  const bottomDescription = `Your new fiber plan total:`;

  const filtersPrice = ( price ) => {
    let newPrice = price?.toString();

    if (newPrice?.endsWith('.99')) {
      newPrice =  newPrice?.slice(0, -3);
    }
    newPrice = parseFloat(newPrice) + netflixPrice
    return newPrice
  }

  const price = filtersPrice(selectedState?.internet?.price?.amount)
  const strikePrice = filtersPrice(selectedState?.internet?.price?.amountNoAutopay)

  return (
    <UpsellStyled>
      <CloseThin onClick={() => handleCloseModal()} data-testid="modal-btn-close" />
      <header className="addon-screen__header">
        <p><img src={getAsset(`/images/logos/netflix-n-logo.svg`)} alt="NetFlix Pro Wi-Fi Bundle" /></p>
        <h3>{title}</h3>
        <p className="addon-screen__header-description">{titleDescription}</p>
      </header>
      <div className="addon-screen__container" data-testid="addon-screen__container">
        <p className="addon-screen__description">{description}</p>
        <div className="addon-screen__content">

        <ColumnTiles />

        </div>
        <div className="addon-screen__bottomDescriptionContainer" >
        <p className="addon-screen__bottomDescription">{bottomDescription}</p>

        <div className='addon-screen__bottomDescription-price'>

          <div>
          <p>

            <span className='addon-screen__bottomDescription-price-amount'>
            <sup className='addon-screen__bottomDescription-price-dollarSign'>$</sup>{price}
            </span>
          </p>


            <p className='addon-screen__bottomDescription-price-strike'>${strikePrice}.99</p>
          </div>
            <p className='addon-screen__bottomDescription-price-cents'>99</p>

          <div className='addon-screen__bottomDescription-price-text'>
          per month with <br />Auto Pay, for 12 months
          </div>
        </div>

        </div>
      </div>
      <div className="duplicateAddOn__ctaContainer">
        <Button secondary className="duplicateAddOn__ctaReplace" onClick={handleReplace}>
          SWITCH TO BUNDLE
        </Button>
        <Button secondary className="duplicateAddOn__ctaCancel" onClick={handleSelectCurrentVas}>
          NO THANKS
        </Button>
      </div>
    </UpsellStyled>
  );
};

UpsellModal.displayName = 'UpsellModal';

export default UpsellModal;
